import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  ApiError,
  commitIntakeToTenant,
  getIntakeDetail,
  readSession,
  rejectIntake,
  type IntakeAuditEvent,
  type IntakeCommitErrorBody,
  type IntakeCommitResponse,
  type IntakeDetail as IntakeDetailData,
  type IntakeRejectErrorBody,
  type IntakeRejectResponse,
} from '../../lib/api.js';

// D2.5.1 — Triage view for a single TenantIntake.
//
// Fetches `GET /api/platform/intakes/:id` and renders:
//   - A header with state, invite email, invited admin, % complete
//   - Action buttons (commit / reject / resend — wired as enabled-with-
//     placeholder; the concrete backends land in D3.3.2, D3.4.1, and a
//     future resend ticket, see Handoff note below)
//   - The full payload as a nested definition list, grouped by step
//   - The audit timeline (every TenantIntakeAuditEvent chronologically)
//
// Scope carve-out: the action buttons render but don't yet dispatch to
// a concrete endpoint — D2.5.1 is the viewing surface; the commit +
// reject flows have their own plan items (D3.3.2 + D3.4.1).

type IntakeDetailProps = {
  config: ClientConfig;
};

type ViewState =
  | { kind: 'loading' }
  | { kind: 'empty_session' }
  | { kind: 'missing_id' }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; intake: IntakeDetailData };

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatState(s: string): string {
  return s
    .split('_')
    .map((p) => (p ? p[0]!.toUpperCase() + p.slice(1) : ''))
    .join(' ');
}

function percentComplete(lastStep: number): number {
  const n = Math.max(1, Math.min(10, Math.round(lastStep)));
  return n * 10;
}

export function IntakeDetail({ config }: IntakeDetailProps): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [view, setView] = useState<ViewState>({ kind: 'loading' });

  useEffect(() => {
    if (!id) {
      setView({ kind: 'missing_id' });
      return;
    }
    const { token } = readSession();
    if (!token) {
      setView({ kind: 'empty_session' });
      return;
    }
    let cancelled = false;
    setView({ kind: 'loading' });
    getIntakeDetail(config.deployment.apiUrl, token, id)
      .then((res) => {
        if (cancelled) return;
        setView({ kind: 'ready', intake: res.intake });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setView({ kind: 'not_found' });
          return;
        }
        const message =
          err instanceof ApiError
            ? `Could not load intake: ${err.message}`
            : err instanceof Error
              ? err.message
              : 'Unexpected error';
        setView({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [config.deployment.apiUrl, id]);

  return (
    <section className="ferro-admin-page">
      <header className="ferro-admin-page__header">
        <h1 className="ferro-section__title">Intake detail</h1>
        <p className="ferro-muted">
          <Link to="/platform/intake">← Back to in-flight intakes</Link>
        </p>
      </header>

      {view.kind === 'loading' ? (
        <p className="ferro-muted" role="status" aria-live="polite">
          Loading intake…
        </p>
      ) : null}

      {view.kind === 'missing_id' ? (
        <p className="ferro-form__error" role="alert">
          No intake id in the URL.
        </p>
      ) : null}

      {view.kind === 'empty_session' ? (
        <p className="ferro-form__error" role="alert">
          Your session has expired. Please sign in again.
        </p>
      ) : null}

      {view.kind === 'not_found' ? (
        <p className="ferro-form__error" role="alert">
          That intake doesn't exist or has been purged.
        </p>
      ) : null}

      {view.kind === 'error' ? (
        <p className="ferro-form__error" role="alert">
          {view.message}
        </p>
      ) : null}

      {view.kind === 'ready' ? (
        <DetailBody
          intake={view.intake}
          apiUrl={config.deployment.apiUrl}
          onIntakeRefresh={(next) =>
            setView({ kind: 'ready', intake: next })
          }
        />
      ) : null}
    </section>
  );
}

type CommitState =
  | { kind: 'idle' }
  | { kind: 'confirming' }
  | { kind: 'submitting' }
  | { kind: 'success'; response: IntakeCommitResponse }
  | { kind: 'error'; message: string; kindTag: string | null };

// D3.4.1 — Reject flow state machine. The confirmation modal carries a
// textarea whose value is part of the state so the submit gate can run
// a client-side length check mirroring the server's Zod schema.
type RejectState =
  | { kind: 'idle' }
  | { kind: 'editing'; reason: string }
  | { kind: 'submitting'; reason: string }
  | { kind: 'success'; response: IntakeRejectResponse }
  | {
      kind: 'error';
      reason: string;
      message: string;
      kindTag: string | null;
    };

/** Matches `MIN_REASON_LENGTH` in packages/core/tenant/intake-reject.ts. */
const REJECT_REASON_MIN = 10;
/** Matches `MAX_REASON_LENGTH` in packages/core/tenant/intake-reject.ts. */
const REJECT_REASON_MAX = 2000;

function buildRejectErrorMessage(err: unknown): {
  message: string;
  kindTag: string | null;
} {
  if (err instanceof ApiError) {
    const body = (err as ApiError & { body?: IntakeRejectErrorBody }).body;
    if (body) {
      if (body.kind === 'illegal_state' && body.state) {
        return {
          message: `Intake is in state '${body.state}', not 'submitted' — refresh and retry.`,
          kindTag: body.kind,
        };
      }
      if (body.kind === 'validation' && body.issues?.length) {
        return {
          message: body.issues.map((i) => i.message).join('; '),
          kindTag: body.kind,
        };
      }
      if (body.kind === 'intake_not_found') {
        return {
          message: `Intake ${body.intakeId ?? ''} was not found.`,
          kindTag: body.kind,
        };
      }
      return { message: err.message, kindTag: body.kind };
    }
    return { message: err.message, kindTag: null };
  }
  if (err instanceof Error) return { message: err.message, kindTag: null };
  return { message: 'Unexpected error', kindTag: null };
}

function buildCommitErrorMessage(err: unknown): {
  message: string;
  kindTag: string | null;
} {
  if (err instanceof ApiError) {
    const body = (err as ApiError & { body?: IntakeCommitErrorBody }).body;
    if (body) {
      if (body.kind === 'illegal_state') {
        return {
          message: `Intake is in state '${body.state}', not 'submitted' — refresh and retry.`,
          kindTag: body.kind,
        };
      }
      if (body.kind === 'kit_assembly_failed') {
        return {
          message:
            'Intake payload is incomplete or inconsistent and cannot be converted to a tenant kit. Ask the prospect to revisit the flagged step.',
          kindTag: body.kind,
        };
      }
      if (body.kind === 'asset_copy_failed') {
        const slots = (body.failures ?? [])
          .map((f) => f.slot)
          .join(', ');
        return {
          message: `Could not copy brand assets (${slots}) to the tenant bucket. Aborted before any DB writes.`,
          kindTag: body.kind,
        };
      }
      if (body.kind === 'provisioning_failed' && body.cause?.kind === 'domain_conflict') {
        return {
          message: `The hostname '${body.cause.hostname}' is already taken by another tenant. Change the subdomain and retry.`,
          kindTag: 'domain_conflict',
        };
      }
      if (body.kind === 'finalize_failed') {
        return {
          message: `Tenant '${body.tenantSlug}' was created, but the intake could not be updated. Click Commit again to finish the transition.`,
          kindTag: body.kind,
        };
      }
      return { message: err.message, kindTag: body.kind };
    }
    return { message: err.message, kindTag: null };
  }
  if (err instanceof Error) return { message: err.message, kindTag: null };
  return { message: 'Unexpected error', kindTag: null };
}

function DetailBody({
  intake,
  apiUrl,
  onIntakeRefresh,
}: {
  intake: IntakeDetailData;
  apiUrl: string;
  onIntakeRefresh: (next: IntakeDetailData) => void;
}): JSX.Element {
  const pct = percentComplete(intake.lastStep);
  const canCommit = intake.state === 'submitted';
  const canReject = intake.state === 'submitted';
  const [commitState, setCommitState] = useState<CommitState>({ kind: 'idle' });
  const [rejectState, setRejectState] = useState<RejectState>({ kind: 'idle' });

  const beginCommit = (): void => {
    if (!canCommit) return;
    setCommitState({ kind: 'confirming' });
  };

  const confirmCommit = async (): Promise<void> => {
    const { token } = readSession();
    if (!token) {
      setCommitState({
        kind: 'error',
        message: 'Your session has expired. Please sign in again.',
        kindTag: null,
      });
      return;
    }
    setCommitState({ kind: 'submitting' });
    try {
      const response = await commitIntakeToTenant(apiUrl, token, intake.id);
      setCommitState({ kind: 'success', response });
      onIntakeRefresh({
        ...intake,
        state: response.intakeState,
        provisionedTenantId: response.tenantId,
      });
    } catch (err) {
      setCommitState({ kind: 'error', ...buildCommitErrorMessage(err) });
    }
  };

  const cancelCommit = (): void => {
    setCommitState({ kind: 'idle' });
  };

  // D3.4.1 — reject handlers.
  const beginReject = (): void => {
    if (!canReject) return;
    setRejectState({ kind: 'editing', reason: '' });
  };

  const setRejectReason = (reason: string): void => {
    setRejectState((prev) => {
      if (prev.kind === 'editing' || prev.kind === 'error') {
        return { ...prev, reason };
      }
      return prev;
    });
  };

  const confirmReject = async (): Promise<void> => {
    const current =
      rejectState.kind === 'editing' || rejectState.kind === 'error'
        ? rejectState
        : null;
    if (!current) return;
    const trimmed = current.reason.trim();
    if (trimmed.length < REJECT_REASON_MIN) {
      setRejectState({
        kind: 'error',
        reason: current.reason,
        message: `Please provide at least ${REJECT_REASON_MIN} characters of feedback.`,
        kindTag: 'validation',
      });
      return;
    }
    const { token } = readSession();
    if (!token) {
      setRejectState({
        kind: 'error',
        reason: current.reason,
        message: 'Your session has expired. Please sign in again.',
        kindTag: null,
      });
      return;
    }
    setRejectState({ kind: 'submitting', reason: current.reason });
    try {
      const response = await rejectIntake(apiUrl, token, intake.id, trimmed);
      setRejectState({ kind: 'success', response });
      onIntakeRefresh({
        ...intake,
        state: response.intakeState,
        rejectionReason: response.rejectionReason,
        rejectedAt: response.rejectedAt,
        updatedAt: response.updatedAt,
      });
    } catch (err) {
      setRejectState({
        kind: 'error',
        reason: current.reason,
        ...buildRejectErrorMessage(err),
      });
    }
  };

  const cancelReject = (): void => {
    setRejectState({ kind: 'idle' });
  };

  const rejectReason =
    rejectState.kind === 'editing' ||
    rejectState.kind === 'submitting' ||
    rejectState.kind === 'error'
      ? rejectState.reason
      : '';
  const rejectTrimmedLength = rejectReason.trim().length;
  const rejectModalOpen =
    rejectState.kind === 'editing' ||
    rejectState.kind === 'submitting' ||
    rejectState.kind === 'error';
  const rejectSubmitDisabled =
    rejectState.kind === 'submitting' ||
    rejectTrimmedLength < REJECT_REASON_MIN ||
    rejectReason.length > REJECT_REASON_MAX;

  return (
    <>
      <section
        className="ferro-form"
        data-testid="intake-detail-summary"
        aria-label="Intake summary"
      >
        <dl className="ferro-dl">
          <dt>Prospect email</dt>
          <dd data-testid="intake-detail-email">{intake.inviteEmail}</dd>
          <dt>State</dt>
          <dd data-testid="intake-detail-state">{formatState(intake.state)}</dd>
          <dt>Progress</dt>
          <dd data-testid="intake-detail-progress">
            Step {intake.lastStep} of 10 ({pct}%)
          </dd>
          <dt>Invited by</dt>
          <dd>{intake.createdByAdminId}</dd>
          <dt>Invite sent</dt>
          <dd>{formatDate(intake.inviteSentAt)}</dd>
          <dt>Invite expires</dt>
          <dd>{formatDate(intake.inviteExpiresAt)}</dd>
          <dt>Last activity</dt>
          <dd>{formatDate(intake.updatedAt)}</dd>
          <dt>Nudges sent</dt>
          <dd data-testid="intake-detail-nudges">{intake.nudgeCount}</dd>
          {intake.submittedAt ? (
            <>
              <dt>Submitted</dt>
              <dd data-testid="intake-detail-submitted">
                {formatDate(intake.submittedAt)}
              </dd>
            </>
          ) : null}
          {intake.rejectedAt ? (
            <>
              <dt>Rejected</dt>
              <dd>{formatDate(intake.rejectedAt)}</dd>
            </>
          ) : null}
          {intake.rejectionReason ? (
            <>
              <dt>Rejection reason</dt>
              <dd data-testid="intake-detail-rejection-reason">
                {intake.rejectionReason}
              </dd>
            </>
          ) : null}
          {intake.provisionedTenantId ? (
            <>
              <dt>Provisioned tenant</dt>
              <dd data-testid="intake-detail-tenant-id">
                {intake.provisionedTenantId}
              </dd>
            </>
          ) : null}
        </dl>
      </section>

      <section
        className="ferro-form__actions"
        aria-label="Intake actions"
        data-testid="intake-detail-actions"
      >
        <button
          type="button"
          className="ferro-btn ferro-btn--primary"
          disabled={!canCommit || commitState.kind === 'submitting' || commitState.kind === 'success'}
          data-testid="intake-detail-commit"
          onClick={beginCommit}
          title={
            canCommit
              ? 'Commit this intake to a new tenant'
              : 'Commit is only available once the prospect submits for review'
          }
        >
          {commitState.kind === 'submitting'
            ? 'Committing…'
            : commitState.kind === 'success'
              ? 'Provisioned ✓'
              : 'Commit to tenant'}
        </button>
        <button
          type="button"
          className="ferro-btn"
          disabled={
            !canReject ||
            rejectState.kind === 'submitting' ||
            rejectState.kind === 'success'
          }
          data-testid="intake-detail-reject"
          onClick={beginReject}
          title={
            canReject
              ? 'Reject this intake and send feedback'
              : 'Reject is only available while the intake is submitted'
          }
        >
          {rejectState.kind === 'submitting'
            ? 'Rejecting…'
            : rejectState.kind === 'success'
              ? 'Rejected ✓'
              : 'Reject with notes'}
        </button>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          data-testid="intake-detail-resend"
        >
          Resend invite email
        </button>
      </section>

      {commitState.kind === 'confirming' ? (
        <section
          className="ferro-form ferro-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="intake-commit-confirm-title"
          data-testid="intake-detail-commit-confirm"
        >
          <h2 id="intake-commit-confirm-title" className="ferro-section__title">
            Provision tenant from this intake?
          </h2>
          <p>
            This will create a new tenant, copy brand assets into the tenant
            bucket, and email a magic-link invite to{' '}
            <strong>{intake.inviteEmail}</strong>.
          </p>
          <p className="ferro-muted">
            Safe to retry: if anything fails mid-way, no partial tenant is
            created.
          </p>
          <div className="ferro-form__actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              onClick={() => {
                void confirmCommit();
              }}
              data-testid="intake-detail-commit-confirm-yes"
            >
              Yes, commit &amp; provision
            </button>
            <button
              type="button"
              className="ferro-btn"
              onClick={cancelCommit}
              data-testid="intake-detail-commit-confirm-cancel"
            >
              Cancel
            </button>
          </div>
        </section>
      ) : null}

      {commitState.kind === 'success' ? (
        <section
          className="ferro-form"
          data-testid="intake-detail-commit-success"
          aria-live="polite"
        >
          <h2 className="ferro-section__title">Tenant provisioned</h2>
          <dl className="ferro-dl">
            <dt>Tenant id</dt>
            <dd data-testid="intake-detail-commit-tenant-id">
              {commitState.response.tenantId}
            </dd>
            <dt>Tenant slug</dt>
            <dd>{commitState.response.tenantSlug}</dd>
            <dt>Modules activated</dt>
            <dd data-testid="intake-detail-commit-module-count">
              {commitState.response.modules.finalSet.length}
            </dd>
            <dt>Owner invite</dt>
            <dd data-testid="intake-detail-commit-invite-status">
              {commitState.response.owner.inviteSent
                ? `Sent to ${commitState.response.owner.email}`
                : `Not sent${commitState.response.owner.inviteError ? ` (${commitState.response.owner.inviteError})` : ''}`}
            </dd>
          </dl>
        </section>
      ) : null}

      {commitState.kind === 'error' ? (
        <p
          className="ferro-form__error"
          role="alert"
          data-testid="intake-detail-commit-error"
        >
          {commitState.message}
        </p>
      ) : null}

      {rejectModalOpen ? (
        <section
          className="ferro-form ferro-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="intake-reject-confirm-title"
          data-testid="intake-detail-reject-confirm"
        >
          <h2 id="intake-reject-confirm-title" className="ferro-section__title">
            Reject intake with notes
          </h2>
          <p>
            The prospect will get an email with the feedback below and a link
            back to the intake portal to edit + resubmit.
          </p>
          <div className="ferro-field">
            <label
              htmlFor="intake-reject-reason"
              className="ferro-field__label"
            >
              Feedback for the prospect (required)
            </label>
            <textarea
              id="intake-reject-reason"
              className="ferro-field__input"
              data-testid="intake-detail-reject-reason"
              rows={6}
              maxLength={REJECT_REASON_MAX}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              aria-describedby="intake-reject-reason-hint"
              disabled={rejectState.kind === 'submitting'}
            />
            <p id="intake-reject-reason-hint" className="ferro-muted">
              {rejectTrimmedLength < REJECT_REASON_MIN
                ? `At least ${REJECT_REASON_MIN} characters required (${rejectTrimmedLength} so far).`
                : `${rejectTrimmedLength} / ${REJECT_REASON_MAX} characters.`}
            </p>
          </div>
          {rejectState.kind === 'error' ? (
            <p
              className="ferro-form__error"
              role="alert"
              data-testid="intake-detail-reject-error"
            >
              {rejectState.message}
            </p>
          ) : null}
          <div className="ferro-form__actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              onClick={() => {
                void confirmReject();
              }}
              disabled={rejectSubmitDisabled}
              data-testid="intake-detail-reject-confirm-yes"
            >
              {rejectState.kind === 'submitting' ? 'Rejecting…' : 'Reject and notify'}
            </button>
            <button
              type="button"
              className="ferro-btn"
              onClick={cancelReject}
              disabled={rejectState.kind === 'submitting'}
              data-testid="intake-detail-reject-confirm-cancel"
            >
              Cancel
            </button>
          </div>
        </section>
      ) : null}

      {rejectState.kind === 'success' ? (
        <section
          className="ferro-form"
          data-testid="intake-detail-reject-success"
          aria-live="polite"
        >
          <h2 className="ferro-section__title">Intake rejected</h2>
          <dl className="ferro-dl">
            <dt>Feedback sent</dt>
            <dd data-testid="intake-detail-reject-notifier-status">
              {rejectState.response.notifier.sent
                ? 'Email delivered to the prospect.'
                : rejectState.response.notifier.attempted
                  ? `Delivery failed: ${rejectState.response.notifier.error ?? 'unknown error'}. The rejection still applied; resend manually if needed.`
                  : 'Notifier not configured; the rejection applied but no email was sent.'}
            </dd>
            <dt>Rejected at</dt>
            <dd>{formatDate(rejectState.response.rejectedAt)}</dd>
          </dl>
        </section>
      ) : null}

      <section aria-label="Payload" data-testid="intake-detail-payload">
        <h2 className="ferro-section__title">Submitted data</h2>
        {Object.keys(intake.payload).length === 0 ? (
          <p className="ferro-muted">Prospect hasn't saved any data yet.</p>
        ) : (
          <PayloadTree payload={intake.payload} />
        )}
      </section>

      <section aria-label="Audit log" data-testid="intake-detail-audit">
        <h2 className="ferro-section__title">Audit timeline</h2>
        {intake.auditEvents.length === 0 ? (
          <p className="ferro-muted">No audit events recorded.</p>
        ) : (
          <ol className="ferro-table">
            {intake.auditEvents.map((event) => (
              <AuditEventRow key={event.id} event={event} />
            ))}
          </ol>
        )}
      </section>
    </>
  );
}

function AuditEventRow({ event }: { event: IntakeAuditEvent }): JSX.Element {
  const transition =
    event.fromState || event.toState
      ? `${event.fromState ?? '∅'} → ${event.toState ?? '∅'}`
      : null;
  return (
    <li
      className="ferro-table__row"
      data-testid={`intake-detail-audit-${event.id}`}
    >
      <div className="ferro-table__body">
        <span className="ferro-table__primary">
          {event.eventType}
          {transition ? ` · ${transition}` : null}
        </span>
        <span className="ferro-table__meta">
          {formatDate(event.createdAt)} · {event.actorKind}
          {event.actorUserId ? ` (${event.actorUserId})` : null}
        </span>
      </div>
    </li>
  );
}

/**
 * Renders the intake payload as a nested definition list. Designed
 * for triage — the admin sees every key/value without having to open
 * a JSON viewer. Arrays and objects recurse; primitives render as-is.
 */
function PayloadTree({
  payload,
}: {
  payload: Record<string, unknown>;
}): JSX.Element {
  return <PayloadValue value={payload} depth={0} />;
}

function PayloadValue({
  value,
  depth,
}: {
  value: unknown;
  depth: number;
}): JSX.Element {
  if (value === null || value === undefined) {
    return <span className="ferro-muted">—</span>;
  }
  if (typeof value === 'boolean') {
    return <span>{value ? 'yes' : 'no'}</span>;
  }
  if (typeof value === 'number' || typeof value === 'string') {
    return <span>{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="ferro-muted">(empty list)</span>;
    }
    return (
      <ol className="ferro-dl">
        {value.map((item, idx) => (
          <li key={idx}>
            <PayloadValue value={item} depth={depth + 1} />
          </li>
        ))}
      </ol>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      return <span className="ferro-muted">(empty)</span>;
    }
    return (
      <dl className="ferro-dl">
        {entries.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>
              <PayloadValue value={v} depth={depth + 1} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return <span>{String(value)}</span>;
}
